import { useEffect, useRef } from "react";

import {
  ATTENTION_WELL_DOT_GLOW_LAYER,
  ATTENTION_WELL_DOT_LAYER,
  ATTENTION_WELL_JUMP_GLOW_LAYER,
  ATTENTION_WELL_JUMP_LAYER,
  ATTENTION_WELL_LAYER,
  ATTENTION_WELL_POINT_LAYER,
  BASE_WELL_LAYER,
  BASE_WELL_RASTER_LAYER,
  BUBBLE_LAYER,
  COVERAGE_LAYER,
  SELECTED_SINGLE_WELL_GLOW_LAYER,
  SELECTED_SINGLE_WELL_LAYER,
  SELECTED_SINGLE_WELL_POINT_GLOW_LAYER,
  SELECTED_SINGLE_WELL_POINT_LAYER,
  SELECTED_WELL_LAYER,
  SELECTED_WELL_POINT_LAYER,
  SHAPEFILE_DETAILS_LABEL_LAYER,
  SHAPEFILE_NAME_LABELS_LAYER,
  SYNC_WELL_LAYER,
  TYPE_LOG_LAYER,
  TYPE_LOG_VERTICAL_LAYER,
  WELL_LAYER,
  WELL_LAYER_POINT
} from "constants/mapLayers.constants";
import { selectLayers, useMapStore } from "stores/map";
import type { MapLayer } from "stores/map";

import { setMapLayersToSession } from "components/map/session/map-layers/setMapLayersToSession";
import { useProjectContext } from "components/project/projects/context";
import { shapefileLayerType } from "components/project/shapefiles/shapefiles.constants";

import {
  FILL_GEOM_BIN_TYPE,
  STROKE_GEOM_BIN_TYPE
} from "../manage-mapbox-geombin-loading/utils/constants";

/**
 * Updates the visibility of the redux.map.layers based on the checked layers keys.
 * Once the layers have been set, the mapbox layers are updated in the useManageMapboxLayerStyles hook.
 * @returns updateMapLayersVisibility
 */
export function useUpdateMapLayersVisibility() {
  const mapLayers = useMapStore(selectLayers) as MapLayer[];
  const setLayers = useMapStore((s) => s.setLayers);
  const { selectedProjectId, selectedViewId } = useProjectContext();

  const mapLayersRef = useRef(mapLayers);
  useEffect(() => {
    mapLayersRef.current = mapLayers;
  }, [mapLayers]);

  function updateMapLayersVisibility(checkedLayerKeys: string[], nextProjectId?: string) {
    const currentProjectId = nextProjectId || selectedProjectId;
    const currentMapLayers = mapLayersRef.current;

    const mapLayersWithUpdatedVisibility = getMapLayersWithUpdatedVisibility(
      checkedLayerKeys,
      currentMapLayers
    );

    // Always update Zustand state first to ensure UI reflects user's selection
    if (mapLayersWithUpdatedVisibility) {
      setLayers(mapLayersWithUpdatedVisibility);
    }

    // Only update session storage when:
    // 1. No view is selected (prevents conflicts with view selections)
    // 2. Not during project load/switch (nextProjectId indicates we're loading from session storage)
    if (!selectedViewId && !nextProjectId && mapLayersWithUpdatedVisibility?.length) {
      setMapLayersToSession({
        mapLayers: mapLayersWithUpdatedVisibility,
        projectId: currentProjectId
      });
    }
  }

  return { updateMapLayersVisibility };
}

/**
 * Layer ID patterns and constants
 */
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const DEPENDENT_LAYERS = {
  // The base-well survey-lines layer and the raster crossover layer (EVA-35)
  // both follow the same "Base Well" checkbox as the well circle layer
  // (which carries the BASE_WELL_LAYER id itself). Unlike the interactive well
  // layer's raster (HZ_WELL_RASTER_LAYER, never declared in getMapLayers and
  // so never registered in this store), BASE_WELL_RASTER_LAYER IS declared in
  // getMapLayers and gets pushed into mapLayers by addMapboxLayers's generic
  // per-layer addLayer() call — so it needs an entry here too, or this
  // mechanism would compute isVisible: false for it regardless of the
  // checkbox state.
  baseWell: [`${BASE_WELL_LAYER}-lines`, BASE_WELL_RASTER_LAYER] as string[],
  well: [
    WELL_LAYER_POINT,
    ATTENTION_WELL_LAYER,
    // The point counterpart of the attention layer. DEFAULT_CHECKED_LAYERS
    // alone is not enough: checkedLayerKeys is persisted, so anyone who has
    // opened the app or saved a view carries a set without this id and the
    // layer computes isVisible false — the highlight exists and is painted
    // "none". It also has to follow the same checkbox as its sibling, or
    // unchecking Wells would hide horizontal highlights while vertical ones
    // kept painting.
    ATTENTION_WELL_POINT_LAYER,
    ATTENTION_WELL_JUMP_GLOW_LAYER,
    ATTENTION_WELL_JUMP_LAYER,
    ATTENTION_WELL_DOT_GLOW_LAYER,
    ATTENTION_WELL_DOT_LAYER
  ] as string[],
  typeLog: [TYPE_LOG_VERTICAL_LAYER] as string[],
  selectedWell: [
    SELECTED_WELL_POINT_LAYER,
    SYNC_WELL_LAYER,
    SELECTED_SINGLE_WELL_GLOW_LAYER,
    SELECTED_SINGLE_WELL_LAYER,
    SELECTED_SINGLE_WELL_POINT_GLOW_LAYER,
    SELECTED_SINGLE_WELL_POINT_LAYER
  ] as string[]
} as const;

const ALWAYS_VISIBLE_LAYERS = [
  BUBBLE_LAYER.toLowerCase(),
  COVERAGE_LAYER.toLowerCase()
] as string[];

const DEPENDENT_LAYER_PARENTS: Record<string, string> = {
  baseWell: BASE_WELL_LAYER,
  well: WELL_LAYER,
  typeLog: TYPE_LOG_LAYER,
  selectedWell: SELECTED_WELL_LAYER
};

const LAYER_ID_TO_PARENT: Record<string, string> = Object.fromEntries(
  Object.entries(DEPENDENT_LAYERS).flatMap(([group, layerIds]) =>
    layerIds.map((layerId) => [layerId, DEPENDENT_LAYER_PARENTS[group]])
  )
);

/**
 * Maps a dependent (child) layer id to the parent row id whose zoom slider
 * it should follow (EVA-237) — the row it depends on for visibility, from
 * DEPENDENT_LAYERS above, so the two mappings can never drift apart.
 */
export function getLayerZoomParent(layerId: string): string | undefined {
  return LAYER_ID_TO_PARENT[layerId];
}

/**
 * Creates a default layer configuration for new shapefile layers
 */
function createDefaultLayerConfig(id: string): MapLayer {
  return {
    id,
    isVisible: true,
    lineWidth: 0,
    circleRadius: 0,
    textSize: 0
  };
}

/**
 * Checks if a layer ID is a valid UUID
 */
function isValidUUID(id: string): boolean {
  return UUID_REGEX.test(id);
}

/**
 * Determines if a layer should be visible based on base layer checks
 * Handles shapefile variants (stroke, point, label, property) and geom bin variants
 */
function isBaseLayerVisible(layerId: string, checkedLayersSet: Set<string>): boolean {
  // Direct match
  if (checkedLayersSet.has(layerId)) {
    return true;
  }

  // Check shapefile stroke variant
  if (checkedLayersSet.has(layerId.replace(shapefileLayerType.stroke, ""))) {
    return true;
  }

  // Check shapefile point variant
  if (checkedLayersSet.has(layerId.replace(shapefileLayerType.point, ""))) {
    return true;
  }

  // Check shapefile label variant (requires global toggle)
  const baseIdFromLabel = layerId.replace(shapefileLayerType.label, "");
  if (
    checkedLayersSet.has(baseIdFromLabel) &&
    checkedLayersSet.has(SHAPEFILE_NAME_LABELS_LAYER)
  ) {
    return true;
  }

  // Check shapefile property variant (requires global toggle)
  const baseIdFromProperty = layerId.replace(shapefileLayerType.property, "");
  if (
    checkedLayersSet.has(baseIdFromProperty) &&
    checkedLayersSet.has(SHAPEFILE_DETAILS_LABEL_LAYER)
  ) {
    return true;
  }

  // Check geom bin stroke variant
  if (checkedLayersSet.has(layerId.replace(STROKE_GEOM_BIN_TYPE, ""))) {
    return true;
  }

  // Check geom bin fill variant
  if (checkedLayersSet.has(layerId.replace(FILL_GEOM_BIN_TYPE, ""))) {
    return true;
  }

  // Check base layer ID (strip suffixes)
  const baseLayerId = layerId
    .replace(STROKE_GEOM_BIN_TYPE, "")
    .replace(FILL_GEOM_BIN_TYPE, "")
    .replace(/-\d+(?!.*-\d+)/, "");

  return checkedLayersSet.has(baseLayerId);
}

/**
 * Checks if a dependent layer should be visible based on its parent layer
 */
function isDependentLayerVisible(
  layerId: string,
  checkedLayersSet: Set<string>
): boolean {
  // Base-well survey lines follow the Base Well checkbox
  if (DEPENDENT_LAYERS.baseWell.includes(layerId)) {
    return checkedLayersSet.has(BASE_WELL_LAYER);
  }

  // Well layer dependencies
  if (DEPENDENT_LAYERS.well.includes(layerId)) {
    return checkedLayersSet.has(WELL_LAYER);
  }

  // Type log dependencies
  if (DEPENDENT_LAYERS.typeLog.includes(layerId)) {
    return checkedLayersSet.has(TYPE_LOG_LAYER);
  }

  // Selected well dependencies
  if (DEPENDENT_LAYERS.selectedWell.includes(layerId)) {
    return checkedLayersSet.has(SELECTED_WELL_LAYER);
  }

  return false;
}

/**
 * Checks if a layer should always be visible regardless of checked state
 */
function isAlwaysVisibleLayer(layerId: string): boolean {
  return ALWAYS_VISIBLE_LAYERS.includes(layerId.toLowerCase());
}

/**
 * Determines the final visibility state for a layer
 */
function calculateLayerVisibility(
  layer: MapLayer,
  checkedLayersSet: Set<string>
): boolean {
  // Always-visible layers (bubble, coverage)
  if (isAlwaysVisibleLayer(layer.id)) {
    return true;
  }

  // Base layer visibility (direct match or variants)
  if (isBaseLayerVisible(layer.id, checkedLayersSet)) {
    return true;
  }

  // Dependent layer visibility (requires parent layer)
  if (isDependentLayerVisible(layer.id, checkedLayersSet)) {
    return true;
  }

  return false;
}

/**
 * Updates visibility for existing layers based on checked layer keys
 */
function updateExistingLayersVisibility(
  mapLayers: MapLayer[],
  checkedLayersSet: Set<string>
): MapLayer[] {
  return mapLayers.map((layer) => ({
    ...layer,
    isVisible: calculateLayerVisibility(layer, checkedLayersSet)
  }));
}

/**
 * Creates missing shapefile variant layers for checked UUIDs
 * Each UUID gets three layers: base, stroke, and label
 */
function createMissingShapefileLayers(
  checkedLayerKeys: string[],
  existingLayerIds: Set<string>
): MapLayer[] {
  const newLayers: MapLayer[] = [];

  for (const key of checkedLayerKeys) {
    if (!isValidUUID(key)) {
      continue;
    }

    // Create base shapefile layer if missing
    if (!existingLayerIds.has(key)) {
      newLayers.push(createDefaultLayerConfig(key));
    }

    // Create stroke variant if missing
    const strokeId = `${key}${shapefileLayerType.stroke}`;
    if (!existingLayerIds.has(strokeId)) {
      newLayers.push(createDefaultLayerConfig(strokeId));
    }

    // Create label variant if missing
    const labelId = `${key}${shapefileLayerType.label}`;
    if (!existingLayerIds.has(labelId)) {
      newLayers.push(createDefaultLayerConfig(labelId));
    }
  }

  return newLayers;
}

/**
 * Main function to update map layer visibility based on checked layer keys
 *
 * This function:
 * 1. Updates visibility for existing layers
 * 2. Handles dependent layers (e.g., well points depend on wells)
 * 3. Manages shapefile variants (stroke, point, label)
 * 4. Auto-creates missing shapefile layers for UUIDs
 * 5. Respects always-visible layers (bubble, coverage)
 *
 * @param checkedLayerKeys - Array of layer IDs that should be visible
 * @param mapLayers - Current array of map layers
 * @returns Updated array of map layers with correct visibility states
 */
export function getMapLayersWithUpdatedVisibility(
  checkedLayerKeys: string[],
  mapLayers: MapLayer[]
): MapLayer[] {
  const checkedLayersSet = new Set(checkedLayerKeys);
  const existingLayerIds = new Set(mapLayers.map((layer) => layer.id));

  // Update visibility for existing layers
  const updatedLayers = updateExistingLayersVisibility(mapLayers, checkedLayersSet);

  // Create any missing shapefile variant layers for checked UUIDs
  const newLayers = createMissingShapefileLayers(checkedLayerKeys, existingLayerIds);

  // Combine existing and new layers
  return [...updatedLayers, ...newLayers];
}
